<template lang="pug">
  .dropzone
    .dz-message
      p {{ message }}
</template>

<script>
import 'dropzone/dist/dropzone.css';
import 'dropzone/dist/basic.css';
import { GetPresignedUrl } from '~/graphql/File.gql';

export default {
  props: {
    path: {
      type: String,
      default: 'uploads'
    },
    isPrivate: {
      type: Boolean,
      default: false
    },
    message: {
      type: String,
      default: 'Drag or Click Upload Your Files'
    },
    value: {
      type: null,
      default: null
    },
    multiple: {
      type: Boolean,
      default: false
    },
    track: {
      type: Boolean,
      default: false
    },
    processQueue: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    filemap: {}, // used to track file urls
    innerValue: []
  }),
  watch: {
    processQueue(value) {
      if (!value) return;

      if (!this.$dropzone.files.length) {
        this.$emit('queuecomplete');
      }

      this.$dropzone.processQueue();
    }
  },
  async mounted() {
    const Dropzone = (await import('dropzone')).default;
    Dropzone.autoDiscover = false;

    const vm = this;
    this.$dropzone = new Dropzone(this.$el, {
      url: 'temp',
      method: 'put',
      autoProcessQueue: this.processQueue,
      accept: (file, done) => {
        vm.$emit('fileAccepted', file);
        done();
      },
      init() {
        this.on('sending', (file, xhr) => {
          xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
          const _send = xhr.send;
          xhr.send = async () => {
            const data = await vm.presign(file);
            // eslint-disable-next-line
            vm.filemap[file.name] = data;
            xhr.open('put', vm.filemap[file.name].url);
            _send.call(xhr, file);
          };
        });
        this.on('success', file => {
          const value = vm.filemap[file.name].publicUrl;
          const payload = vm.filemap[file.name];
          delete vm.filemap[file.name];
          if (!this.multiple) {
            vm.$emit('input', value);
            vm.$emit('fileUploaded', { file, payload });
            return;
          }

          this.innerValue.push(value);
          vm.$emit('input', this.innerValue);
          vm.$emit('fileUploaded', { file, payload });
        });

        this.on('queuecomplete', () => {
          vm.$emit('queuecomplete');
        });
      }
    });
  },
  methods: {
    async presign(file) {
      const ext = file.name.match(/\.[0-9a-z]+$/i)[0];
      const fileName = `${file.name.replace(ext, '')}_${Number(Date.now())}${ext}`;
      const { data } = await this.$api.mutate({
        mutation: GetPresignedUrl,
        variables: {
          input: {
            key: `${this.path}/${fileName}`,
            isPrivate: this.isPrivate,
            contentType: file.type,
            wantToken: this.track
          }
        }
      });

      return data.getPresignedUrl;
    }
  }
};
</script>

<style lang="stylus" scoped>
body
  .dropzone
    border: $border dashed $clBlack
    font-family: unquote('"Graphik", sans-serif')
    color: $clBlack
    display: flex
    flex-wrap: wrap
    align-items: center
    justify-content: center

    *
      border-radius: 0 !important

    &:hover
      border-color: $clPrimary
      background: $clWhite

    .dz-message
      margin: 1em 0

    .dz-preview
      width: unitRes(80, 160)
      height: unitRes(80, 160)
      margin: unitRes(8, 16)
      border: unitRes(1, 2) solid $clBlack

      &:hover
        border-color: $clPrimary

      .dz-details
        background: $clPrimary

      .dz-remove
        border: none
        border-bottom: 2px solid white
        padding: 0

      .dz-image
        display: flex
        justify-content: center
        align-items: center

        img
          max-width: 100%
          height: auto

    .dz-error-message
      background: $clDanger

    .dz-progress
      .dz-upload
        background: $clSecondary
</style>
